
import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  TimeScale,
  ChartData,
  ChartOptions,
  TimeUnit,
  BarController,
  LineController,
  Chart
} from 'chart.js';
import { Chart as ReactChart } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

// Register all required components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController
);

interface TimelineChartProps {
  data: any[];
}

interface ExtendedChartData {
  x: string;
  y: number;
  deployments?: any[];
  incidents?: any[];
}

export function TimelineChart({ data }: TimelineChartProps) {
  const chartRef = useRef<ChartJS | null>(null);

  // Add default height if container is not sized
  const containerStyle = {
    minHeight: '400px',
    width: '100%',
    position: 'relative' as const
  };

  // Clean up chart instance on unmount to prevent duplicated canvas errors
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  if (!data || data.length === 0) {
    return <div style={containerStyle} className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100">No data available</div>;
  }

  const chartData = {
    labels: data.map(item => new Date(item.date)),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Deployments',
        data: data.map(item => ({
          x: new Date(item.date),
          y: item.deployments || 0,
          deployments: item.deploymentDetails || [],
          incidents: item.incidentDetails || []
        })),
        backgroundColor: data.map(item => 
          (item.incidentDetails || []).length > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)'
        ),
        borderColor: data.map(item => 
          (item.incidentDetails || []).length > 0 ? 'rgb(239, 68, 68)' : 'rgb(59, 130, 246)'
        ),
        borderWidth: 1,
        borderRadius: 4,
        yAxisID: 'y'
      },
      {
        type: 'line' as const,
        label: 'Incidents',
        data: data.map(item => ({
          x: new Date(item.date),
          y: (item.incidentDetails || []).length,
          incidents: item.incidentDetails || []
        })),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1',
        fill: true
      }
    ]
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'day' as TimeUnit,
          displayFormats: {
            day: 'MMM d'
          }
        },
        title: {
          display: true,
          text: 'Date'
        },
        grid: {
          display: true,
          drawOnChartArea: true,
          drawTicks: true,
        }
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Number of Deployments'
        },
        min: 0,
        grid: {
          drawOnChartArea: true,
        },
        ticks: {
          stepSize: 1
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Number of Incidents'
        },
        min: 0,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const dataset = context.dataset;
            const value = context.parsed.y;
            if (dataset.type === 'bar') {
              return `Deployments: ${value}`;
            } else {
              return `Incidents: ${value}`;
            }
          }
        }
      },
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle'
        }
      }
    }
  };

  // Fix TypeScript error by using a more generic type for the chart ref
  const getChartRef = (chart: Chart | null) => {
    chartRef.current = chart;
  };

  return (
    <div style={containerStyle} className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
      <ReactChart 
        ref={getChartRef as any}
        type="bar" 
        data={chartData} 
        options={options} 
      />
    </div>
  );
}
